/**
 * The random source behind every generated die (docs/design.md, Play mode): `crypto.getRandomValues`
 * with rejection sampling, so every face is equally likely and no seed is exposed. Tests and the
 * physical die entry supply their own `DiceSupply`.
 */

/** Rolls one die of the given faces and returns 1..faces. */
export type DiceSupply = (faces: number) => number;

/** A die from the platform's cryptographic random source. */
export const cryptoDice: DiceSupply = (faces) => {
  if (!Number.isInteger(faces) || faces < 1) throw new Error(`a die needs at least one face, got ${faces}`);
  if (faces === 1) return 1;
  const buffer = new Uint32Array(1);
  // Rejection sampling: discard values from the top partial range so every face is equally likely.
  const limit = Math.floor(0x1_0000_0000 / faces) * faces;
  for (;;) {
    crypto.getRandomValues(buffer);
    const v = buffer[0]!;
    if (v < limit) return (v % faces) + 1;
  }
};

/** A supply that hands out the given results in order (tests; the physical die entry), then throws. */
export function fixedDice(results: number[]): DiceSupply & { remaining: () => number } {
  const queue = [...results];
  const supply = ((faces: number) => {
    const next = queue.shift();
    if (next === undefined) throw new Error(`no die result left for a d${faces}`);
    return Math.min(faces, Math.max(1, Math.trunc(next)));
  }) as DiceSupply & { remaining: () => number };
  supply.remaining = () => queue.length;
  return supply;
}
